import { splitSources } from '../split-sources';
import type { BizyChatMessage } from '../types/chat';
import type { BizyUserMessage } from '../types/conversations';
import type { BizyAssistantMessageNode } from '../types/nodes';

// ─── hydrateMessages ─────────────────────────────────────────────────
//
// Turn persisted API rows (`BizyUserMessage`) into the rendered
// `BizyChatMessage[]` the bubble components consume. Each row expands into:
//   - a user bubble (skipped when `userMessage` is null/blank — proactive turns)
//   - an assistant bubble whose `nodes` come from `assistantMessage`, or a
//     synthesized single markdown node from `bizyResponse` for legacy rows
//     written before the typed-Document rollout.
//
// `makeKey` mints stable per-bubble keys; pass a host generator (or default to
// the row id) so keys stay stable across re-hydration.

export interface HydrateMessagesOptions {
	/** Stable per-bubble key generator. Defaults to deriving from the row id. */
	makeKey?: (message: BizyUserMessage, role: 'user' | 'assistant') => string;
}

function assistantNodes(message: BizyUserMessage): BizyAssistantMessageNode[] {
	const persisted = message.assistantMessage;
	if (persisted && persisted.length > 0) return persisted;
	// Legacy fallback — wrap the plain text in a synthetic markdown node so the
	// renderer's switch always has a node to walk.
	return [{ type: 'markdown', payload: { content: message.bizyResponse } }];
}

export function hydrateMessages(apiMessages: BizyUserMessage[], options: HydrateMessagesOptions = {}): BizyChatMessage[] {
	const keyFor = options.makeKey ?? ((message, role) => (role === 'user' ? `${message.id}:user` : message.id));
	const out: BizyChatMessage[] = [];

	for (const m of apiMessages) {
		if (m.userMessage && m.userMessage.trim().length > 0) {
			out.push({
				role: 'user',
				key: keyFor(m, 'user'),
				content: m.userMessage,
				timestamp: m.createdAt ? new Date(m.createdAt) : undefined,
			});
		}

		out.push({
			role: 'assistant',
			key: keyFor(m, 'assistant'),
			id: m.id,
			content: m.bizyResponse ?? '',
			nodes: assistantNodes(m),
			feedbackType: m.feedbackType ?? undefined,
			emotion: m.emotion,
		});
	}

	return out;
}

// ─── extractConversationSources ──────────────────────────────────────
//
// Roll up every cited source across a conversation. Pulls each assistant
// message's markdown nodes, finds the trailing citation block via
// `splitSources`, then yanks `[title](url)` links out of it. Deduplicates by
// URL; preserves first-cited order.

export interface ConversationSource {
	title: string;
	url: string;
}

// Permissive markdown-link regex. Accepts absolute http(s) URLs and root-
// relative paths (Bizy emits source links as paths against the Bonusly host).
const MARKDOWN_LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^)\s]+|\/[^)\s]+)\)/g;

export function extractConversationSources(messages: BizyChatMessage[]): ConversationSource[] {
	const seen = new Set<string>();
	const out: ConversationSource[] = [];

	for (const message of messages) {
		if (message.role !== 'assistant') continue;
		for (const node of message.nodes) {
			if (node.type !== 'markdown') continue;
			const { sources: block } = splitSources(node.payload.content);
			if (!block) continue;

			for (const match of block.matchAll(MARKDOWN_LINK_RE)) {
				const url = match[2];
				if (seen.has(url)) continue;
				seen.add(url);
				out.push({ title: match[1].trim(), url });
			}
		}
	}

	return out;
}
